import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import apiService from '../services/api';
import OpenWhatsAppButton, { openWhatsAppApp } from './OpenWhatsAppButton';
import './PayCobranca.css';

const STATUS_LABELS = {
  pendente: 'Pendente',
  enviada: 'Enviada',
  aguardando_confirmacao: 'Aguardando confirmação',
  incompleto: 'Pagamento incompleto',
  paga: 'Paga',
  cancelada: 'Cancelada',
};

/** WhatsApp para envio de comprovante PIX */
const COMPROVANTE_WHATSAPP = '5511989141662';

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value) || 0
  );

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value.includes('T') ? value : `${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
};

const buildComprovanteMessage = ({ clientName, title, amount, chargeId }) =>
  [
    `Olá! Já realizei o pagamento via PIX.`,
    '',
    `Cliente: ${clientName || '—'}`,
    `Cobrança: ${title || '—'}`,
    `Valor: ${amount}`,
    `Código: ${chargeId}`,
    '',
    `Segue o comprovante.`,
  ].join('\n');

const PayCobranca = () => {
  const { chargeId = '' } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPayConfirm, setShowPayConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const json = await apiService.getCobrancaPublic(chargeId);
        if (cancelled) return;
        setPayload(json?.data || json);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Cobrança não encontrada');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (chargeId) load();
    return () => {
      cancelled = true;
    };
  }, [chargeId]);

  const copyPix = async () => {
    const text = payload?.charge?.pix_payload;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const charge = payload?.charge;
  const merchantName = payload?.merchantName || 'SENDINGTO';
  const clientName = payload?.clientName || 'Cliente';
  const whatsappPhone = payload?.whatsappNumber || COMPROVANTE_WHATSAPP;

  const comprovanteMessage = useMemo(() => {
    if (!charge) return '';
    return buildComprovanteMessage({
      clientName,
      title: charge.titulo,
      amount: formatCurrency(charge.valor),
      chargeId: charge.id || chargeId,
    });
  }, [charge, clientName, chargeId]);

  const reportPayment = async () => {
    if (!charge) return;
    setShowPayConfirm(false);

    try {
      setReporting(true);
      setError(null);
      const json = await apiService.reportCobrancaPayment(chargeId);
      const updated = json?.data || json;
      setPayload((prev) => ({
        ...prev,
        charge: { ...(prev?.charge || {}), ...updated },
      }));
    } catch (err) {
      setError(err?.message || null);
    } finally {
      setReporting(false);
    }

    openWhatsAppApp(whatsappPhone, comprovanteMessage);
  };

  if (loading) {
    return (
      <div className="pay-page">
        <div className="pay-page__inner pay-page__center">
          <img src="/logo.png" alt="SendingTo" className="pay-page__logo-lg" />
          <p className="pay-page__muted">Carregando cobrança…</p>
        </div>
      </div>
    );
  }

  if (error && !payload?.charge) {
    return (
      <div className="pay-page">
        <div className="pay-page__inner pay-page__center">
          <img src="/logo.png" alt="SendingTo" className="pay-page__logo-lg" />
          <h1 className="pay-page__error-title">Cobrança indisponível</h1>
          <p className="pay-page__muted">{error || 'Não encontramos esta cobrança.'}</p>
          <Link to="/login" className="pay-page__link">
            Ir para o admin
          </Link>
        </div>
      </div>
    );
  }

  const isPaid = charge.status === 'paga';
  const isAwaiting = charge.status === 'aguardando_confirmacao';
  const isIncomplete = charge.status === 'incompleto';
  const isCancelled = charge.status === 'cancelada';
  const valorRestante =
    charge.valor_restante != null
      ? Number(charge.valor_restante)
      : Math.max(0, (Number(charge.valor) || 0) - (Number(charge.valor_pago) || 0));
  const valorPago = Number(charge.valor_pago) || 0;
  const paymentRegistered = isPaid || isAwaiting;
  const pixPayload = charge.pix_payload || '';

  return (
    <div className="pay-page">
      <div className="pay-page__inner">
        <div className="pay-page__topbar">
          <div className="pay-page__brand">
            <img src="/logo.png" alt="SendingTo" className="pay-page__logo" />
            <div>
              <p className="pay-page__brand-name">SendingTo</p>
              <p className="pay-page__brand-sub">Cobrança PIX</p>
            </div>
          </div>
          <span className="pay-page__pill">Somente PIX</span>
        </div>

        <article className="pay-card">
          <header className="pay-card__hero">
            <p className="pay-card__eyebrow">Cobrança SendingTo</p>
            <h1 className="pay-card__title">{charge.titulo}</h1>
            <p className="pay-card__subtitle">{clientName}</p>
          </header>

          <div className="pay-card__body">
            <div className="pay-card__amount-row">
              <div>
                <p className="pay-card__label">
                  {isIncomplete ? 'Restante a pagar' : 'Valor a pagar'}
                </p>
                <p className="pay-card__amount">
                  {formatCurrency(isIncomplete ? valorRestante : charge.valor)}
                </p>
                {isIncomplete || valorPago > 0 ? (
                  <p className="pay-page__muted">
                    Total {formatCurrency(charge.valor)} · Já pago {formatCurrency(valorPago)}
                  </p>
                ) : null}
              </div>
              <div className="pay-card__amount-meta">
                <span className={`pay-status pay-status--${charge.status}`}>
                  {STATUS_LABELS[charge.status] || charge.status}
                </span>
                <p className="pay-card__due">Vencimento {formatDate(charge.vencimento)}</p>
              </div>
            </div>

            {charge.descricao ? (
              <p className="pay-card__desc">{charge.descricao}</p>
            ) : null}

            {isPaid ? (
              <div className="pay-alert pay-alert--success">
                <strong>Pagamento registrado com sucesso</strong>
                <p>O pagamento de {formatCurrency(charge.valor)} foi confirmado pela SendingTo.</p>
              </div>
            ) : null}

            {isIncomplete ? (
              <div className="pay-alert pay-alert--warn">
                <strong>Pagamento incompleto</strong>
                <p>
                  Já recebemos {formatCurrency(valorPago)}. Ainda falta{' '}
                  {formatCurrency(valorRestante)} para quitar esta cobrança.
                </p>
              </div>
            ) : null}

            {isAwaiting ? (
              <div className="pay-alert pay-alert--warn">
                <strong>Pagamento registrado com sucesso</strong>
                <p>
                  Recebemos seu aviso. Envie o comprovante no WhatsApp se ainda não enviou. A
                  confirmação final é feita pela SendingTo.
                </p>
                <OpenWhatsAppButton
                  phone={whatsappPhone}
                  message={comprovanteMessage}
                  label="Abrir WhatsApp"
                  variant="primary"
                  fullWidth
                  className="pay-alert__whatsapp"
                />
              </div>
            ) : null}

            {isCancelled ? (
              <div className="pay-alert pay-alert--danger">
                <strong>Cobrança cancelada</strong>
                <p>Esta cobrança não está mais disponível para pagamento.</p>
              </div>
            ) : null}

            {!paymentRegistered && !isCancelled ? (
              <>
                <div className="pay-alert pay-alert--info">
                  <strong>Pague apenas com PIX</strong>
                  <p>
                    Escaneie o QR Code ou copie o código no app do seu banco. Depois toque em “Já
                    paguei via PIX” para registrar e enviar o comprovante no WhatsApp.
                  </p>
                </div>

                <section className="pay-pix">
                  <div className="pay-pix__head">
                    <div className="pay-pix__icon" aria-hidden>
                      PIX
                    </div>
                    <div>
                      <p className="pay-pix__title">Pagamento via PIX</p>
                      <p className="pay-pix__sub">Única forma de pagamento disponível</p>
                    </div>
                  </div>

                  <div className="pay-pix__grid">
                    <div className="pay-pix__qr">
                      {pixPayload ? (
                        <QRCodeSVG
                          value={pixPayload}
                          size={168}
                          bgColor="#FFFFFF"
                          fgColor="#0A0A0A"
                          level="M"
                        />
                      ) : (
                        <p className="pay-page__muted">QR Code indisponível</p>
                      )}
                    </div>

                    <div className="pay-pix__details">
                      <div>
                        <p className="pay-card__label">Valor</p>
                        <p className="pay-pix__value">{formatCurrency(charge.valor)}</p>
                        <p className="pay-page__muted">Recebedor: {merchantName}</p>
                      </div>

                      <div>
                        <p className="pay-card__label">PIX Copia e Cola</p>
                        <div className="pay-pix__code">{pixPayload || '—'}</div>
                      </div>

                      <button type="button" className="pay-btn" onClick={copyPix}>
                        {copied ? 'Copiado!' : 'Copiar código PIX'}
                      </button>
                    </div>
                  </div>
                </section>

                {error ? <p className="pay-card__error">{error}</p> : null}

                <button
                  type="button"
                  className="pay-btn pay-btn--cta"
                  onClick={() => setShowPayConfirm(true)}
                  disabled={reporting}
                >
                  {reporting ? 'Abrindo WhatsApp…' : 'Já paguei via PIX'}
                </button>
                <p className="pay-card__footnote">
                  Isso registra o pagamento e abre o WhatsApp (+55 11 98914-1662) para enviar o
                  comprovante. A confirmação final é feita pela SendingTo.
                </p>
              </>
            ) : null}
          </div>
        </article>
      </div>

      {showPayConfirm ? (
        <div
          className="pay-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pay-confirm-title"
        >
          <div className="pay-confirm">
            <h2 id="pay-confirm-title" className="pay-confirm__title">
              Confirmar pagamento
            </h2>
            <p className="pay-confirm__message">
              Você realmente já efetuou o pagamento via PIX de{' '}
              <strong>{formatCurrency(charge.valor)}</strong>?
            </p>
            <div className="pay-confirm__actions">
              <button
                type="button"
                className="pay-btn pay-btn--ghost"
                onClick={() => setShowPayConfirm(false)}
                disabled={reporting}
              >
                Não
              </button>
              <button
                type="button"
                className="pay-btn pay-btn--cta"
                onClick={reportPayment}
                disabled={reporting}
              >
                {reporting ? 'Abrindo WhatsApp…' : 'Sim, já paguei'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PayCobranca;
